import { action, computed, makeObservable, observable } from 'mobx';
import API from '../api/API';
import RootStore from './RootStore';
import SpinnerStore from './SpinnerStore';
import { FULL_PAGE } from '../components/SpinnerWrapper';
import { StepName } from 'src/config/Wizard';
import JSZip from 'jszip';


export interface EventPhoto {
  photoUrl: string;
  eventId: number;
}

export interface AdCampaign {
  type: 'ad_campaign';
  id: number;
  key: string;
  title: string;
  description: string;
  imageUrl: string;
  cta: { label: string; url: string } | null;
}

export interface EventPromotion extends EventPhoto {
  type: 'event';
  key: string;
}

export type PromotionItem = AdCampaign | EventPromotion;

export const parseAdCampaigns = (response: string): AdCampaign[] => {
  try {
    const campaigns = JSON.parse(response)?.campaigns;
    if (!Array.isArray(campaigns)) return [];

    return campaigns.flatMap((campaign: any) => {
      if (!Number.isInteger(campaign?.id) ||
          typeof campaign?.key !== 'string' ||
          typeof campaign?.title !== 'string' ||
          typeof campaign?.description !== 'string' ||
          typeof campaign?.image_url !== 'string') {
        return [];
      }

      const cta = typeof campaign?.cta?.url === 'string' && campaign.cta.url.length > 0
        ? { label: typeof campaign.cta.label === 'string' ? campaign.cta.label : '', url: campaign.cta.url }
        : null;
      return [{
        type: 'ad_campaign' as const,
        id: campaign.id,
        key: campaign.key,
        title: campaign.title,
        description: campaign.description,
        imageUrl: campaign.image_url,
        cta
      }];
    });
  } catch {
    return [];
  }
};

export const buildPromotionItems = (
  campaigns: AdCampaign[],
  eventPhotos: EventPhoto[]
): PromotionItem[] => [
  ...campaigns,
  ...eventPhotos.map(photo => ({ ...photo, type: 'event' as const, key: `event:${photo.eventId}` }))
];

export class EventPhotoStore {
  @observable public eventPhotos: EventPhoto[] = [];
  @observable public adCampaigns: AdCampaign[] = [];
  @observable public selectedCampaignId: number | null = null;
  @observable public error: string | null = null;
  @observable public eventPhotosMap: Map<number, EventPhoto> = new Map();
  private readonly pendingPhotoLoads = new Set<number>();
  private readonly missingPhotoIds = new Set<number>();

  private readonly spinnerStore: SpinnerStore;
  wizardStore: any;

  constructor(readonly rootStore: RootStore) {
    makeObservable(this);
    this.spinnerStore = rootStore.spinnerStore;
  }

  @action
  public loadEventPhoto = async (eventId: number) => {
    this.spinnerStore.show(FULL_PAGE);
    try {
      const blob = await API.getBlob(`/events/${eventId}/photo`);
      const photoUrl = URL.createObjectURL(blob);
      this.setEventPhoto(eventId, { photoUrl, eventId });
    } catch (err) {
      console.error(err);
      this.error = 'Failed to load event photo.';
    } finally {
      this.spinnerStore.hide(FULL_PAGE);
    }
  };

  public loadEventPhotoQuietly = async (eventId: number) => {
    if (this.getEventPhoto(eventId) || this.pendingPhotoLoads.has(eventId) || this.missingPhotoIds.has(eventId)) return;
    this.pendingPhotoLoads.add(eventId);
    try {
      const blob = await API.getBlob(`/events/${eventId}/photo`);
      this.setEventPhoto(eventId, { photoUrl: URL.createObjectURL(blob), eventId });
    } catch (err) {
      console.error(err);
      this.missingPhotoIds.add(eventId);
    } finally {
      this.pendingPhotoLoads.delete(eventId);
    }
  };

  @action
  private setEventPhoto = (eventId: number, photo: EventPhoto) => {
    this.eventPhotosMap.set(eventId, photo);
  };

  @action
  public clearEventPhoto = (eventId: number) => {
    this.eventPhotosMap.delete(eventId);
    this.missingPhotoIds.delete(eventId);
  };

  @action
  public clearAllEventPhotos = () => {
    this.eventPhotosMap.clear();
    this.missingPhotoIds.clear();
  };

  @action
  public selectCampaign = (campaignId: number | null) => {
    this.selectedCampaignId = campaignId;
  };

  public getEventPhoto(eventId: number): EventPhoto | null {
    return this.eventPhotosMap.get(eventId) ||
      this.eventPhotos.find(photo => photo.eventId === eventId) ||
      null;
  }

  @action
  public loadEventPhotos = async (userID: number) => {
    this.spinnerStore.show(FULL_PAGE);
    this.error = null;
    this.adCampaigns = [];
    this.eventPhotos = [];
    try {
      try {
        const response = await API.get(`/ad-campaigns/${userID}`);
        this.setAdCampaigns(parseAdCampaigns(response));
      } catch (err) {
        console.error(err);
      }

      try {
        const zipBlob = await API.getBlob(`/all-event-photos/${userID}`);
  
        const jsZip = await JSZip.loadAsync(zipBlob);
        const photoUrls: { photoUrl: string; eventId: number }[] = [];
  
        for (const fileName of Object.keys(jsZip.files)) {
          const zipEntry = jsZip.files[fileName];
          if (zipEntry.dir) continue;
          const eventIdMatch = fileName.match(/event-(\d+)\./);
          if (!eventIdMatch) continue;

          const fileData = await zipEntry.async('blob');
          const photoUrl = URL.createObjectURL(fileData);
          photoUrls.push({ photoUrl, eventId: parseInt(eventIdMatch[1], 10) });
        }
        
        this.setEventPhotos(photoUrls);
      } catch (err) {
        console.error(err);
      }

      if (this.promotionItems.length === 0) {
        this.error = 'No promotions found.';
      }
    } finally {
      this.spinnerStore.hide(FULL_PAGE);
    }
  };

  @action
  private setAdCampaigns = (campaigns: AdCampaign[]) => {
    this.adCampaigns = campaigns;
  };

  @action
  private setEventPhotos = (photos: { photoUrl: string; eventId: number }[]) => {
    this.eventPhotos = photos.map((photo) => ({
      photoUrl: photo.photoUrl || '',
      eventId: photo.eventId
    }));
    this.eventPhotosMap.clear();
    this.eventPhotos.forEach(photo => this.eventPhotosMap.set(photo.eventId, photo));
  };
  

  @action
  public clearError = () => {
    this.error = null;
  };

  @computed
  get hasPhotos(): boolean {
    return this.eventPhotos.length > 0;
  }

  @computed
  get promotionItems(): PromotionItem[] {
    return buildPromotionItems(this.adCampaigns, this.eventPhotos);
  }
}

export default EventPhotoStore;
